import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Shield, Users, TrendingUp, Award, CheckCircle, Calendar, Star, MessageCircle, ArrowRight, BadgeCheck } from "lucide-react";
import { Button } from "@/components/ui/button";

const PHONE = "7410121121";
const WHATSAPP_LINK = `https://wa.me/91${PHONE}?text=Hi,%20I%20want%20to%20know%20more%20about%20FutureSanchay`;

const stats = [
  { value: "5+", label: "Years in Business", icon: Calendar },
  { value: "40,000+", label: "Policies Reviewed", icon: Shield },
  { value: "₹2,200+ Cr", label: "Premium Managed", icon: TrendingUp },
  { value: "75+", label: "Team Members", icon: Users },
  { value: "4.9", label: "Google Rating", icon: Star },
];

const values = [
  {
    title: "Honest Reviews Over Sales",
    description: "We analyze your policy first. We don't sell unless it actually makes sense for you. Your financial health is our priority."
  },
  {
    title: "Long-Term Relationships",
    description: "We're not here for one-time commissions. Our goal is to be your family's trusted insurance partner for decades."
  },
  {
    title: "IRDAI Compliance First",
    description: "We follow all IRDAI guidelines strictly. No misleading claims, no pressure tactics, just transparent advice."
  },
  {
    title: "Service Beyond Sales",
    description: "Premium reminders, claim assistance, policy servicing - we're there when you need us. Zero spam guaranteed."
  }
];

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-white page-transition" data-testid="about-page">
      <Helmet>
        <title>About FutureSanchay | IRDAI Certified LIC Advisors | 40,000+ Families</title>
        <meta name="description" content="FutureSanchay (Wise Owl Insurance) - India's trusted LIC policy review platform since 2019. IRDAI certified experts. 40,000+ families helped. ₹2,200 Cr premiums managed." />
        <link rel="canonical" href="https://futuresanchay.com/about" />
      </Helmet>
      {/* Hero Header */}
      <section className="pt-8 pb-12 md:pt-12 md:pb-20 relative overflow-hidden" data-testid="about-hero">
        {/* Animated background */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-sky-50 rounded-full blur-3xl animate-pulse opacity-60"></div>
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-emerald-50 rounded-full blur-3xl animate-pulse opacity-50" style={{animationDelay: '1s'}}></div>
        </div>

        <div className="container-main relative z-10">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-6 animate-fade-in-down">
            <Link to="/" className="hover:text-sky-500 transition-colors">Home</Link>
            <span>/</span>
            <span className="text-gray-900 font-medium">About Us</span>
          </div>

          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-sky-50 text-sky-600 px-4 py-2 rounded-full text-sm font-medium mb-4 animate-fade-in-up">
              <BadgeCheck className="w-4 h-4" />
              IRDAI Certified Experts
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-6 animate-fade-in-up delay-100">
              About <span className="text-sky-500">FutureSanchay</span>
            </h1>
            <p className="text-lg text-gray-600 animate-fade-in-up delay-200">
              Since 2019, we've been helping Indian families make <span className="font-semibold text-gray-800">informed LIC decisions</span>. We're not typical agents - we're your policy review partners who prioritize your interests over commissions.
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-gray-50" data-testid="stats-section">
        <div className="container-main">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {stats.map((stat, index) => (
              <div 
                key={index} 
                className="text-center animate-bounce-in" 
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`stat-${index}`}
              >
                <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-md hover:shadow-lg hover:scale-110 transition-all duration-300">
                  <stat.icon className="w-7 h-7 text-sky-500" />
                </div>
                <p className="text-2xl md:text-3xl font-bold text-gray-900">
                  {stat.value}
                </p>
                <p className="text-sm text-gray-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-16 md:py-24" data-testid="story-section">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Our Story
              </h2>
              <div className="space-y-4 text-gray-600">
                <p>
                  FutureSanchay (formerly Wise Owl Insurance) was founded with a simple belief: 
                  <span className="font-semibold text-gray-800"> Indian families deserve honest guidance - not aggressive sales pitches.</span>
                </p>
                <p>
                  We noticed that most LIC policyholders didn't understand what they had bought. Agents focused on selling, not educating. Many families ended up with wrong policies - overpaying for inadequate coverage.
                </p>
                <p>
                  That's why we built a <span className="font-semibold text-sky-500">Policy Review Platform</span>. Before recommending anything new, we analyze your existing policies. We tell you honestly - should you continue, modify, or exit?
                </p>
                <p className="font-semibold text-gray-800 bg-sky-50 p-4 rounded-xl border border-sky-100">
                  "We don't sell unless a policy actually makes sense for YOU."
                </p>
              </div>
            </div>
            <div className="relative animate-fade-in-right">
              <img
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&q=85"
                alt="Professional team collaboration"
                className="rounded-3xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-gradient-to-br from-sky-500 to-sky-600 rounded-2xl p-6 shadow-xl text-white animate-bounce-in delay-300">
                <p className="text-lg font-medium">Since</p>
                <p className="text-4xl font-bold">2019</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 md:py-24 bg-gray-50" data-testid="values-section">
        <div className="container-main">
          <div className="text-center mb-12 animate-fade-in-up">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Philosophy
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              These principles guide everything we do at FutureSanchay.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {values.map((value, index) => (
              <div 
                key={index} 
                className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-2 animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`value-${index}`}
              >
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {value.title}
                </h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-16 md:py-24" data-testid="services-section">
        <div className="container-main">
          <div className="text-center mb-12 animate-fade-in-up">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What We Do For You
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Free Policy Review",
                description: "Upload your existing policies. We analyze them for free and tell you what's good and what's not."
              },
              {
                title: "Honest Recommendations",
                description: "Based on your goals and financial situation, we suggest the right path - even if it means not buying anything new."
              },
              {
                title: "Documentation Help",
                description: "We guide you through the entire application process, ensuring no delays or rejections."
              },
              {
                title: "Premium Reminders",
                description: "Never miss a premium payment. We track and remind you to keep your policies active."
              },
              {
                title: "Policy Servicing",
                description: "Address changes, nomination updates, loan requests - we handle all servicing needs."
              },
              {
                title: "Claim Assistance",
                description: "When the time comes, we support your family through the entire claim process."
              }
            ].map((service, index) => (
              <div 
                key={index} 
                className="flex items-start gap-4 animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`service-${index}`}
              >
                <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0 hover:scale-110 transition-transform">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">
                    {service.title}
                  </h3>
                  <p className="text-sm text-gray-600">{service.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-sky-500 via-sky-600 to-emerald-500 relative overflow-hidden" data-testid="cta-section">
        {/* Animated shapes */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 left-10 w-32 h-32 bg-white/10 rounded-full animate-float blur-xl"></div>
          <div className="absolute bottom-10 right-10 w-40 h-40 bg-white/10 rounded-full animate-float blur-xl" style={{animationDelay: '1s'}}></div>
        </div>
        
        <div className="container-main text-center relative z-10">
          <div className="animate-fade-in-up">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Get Your Policy Reviewed?
            </h2>
            <p className="text-sky-100 max-w-2xl mx-auto mb-8 text-lg">
              Let's discuss how we can help secure your family's financial future. <span className="font-semibold text-white">100% Free. Zero Spam.</span>
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up delay-200">
            <Link to="/contact">
              <Button className="bg-white text-sky-600 hover:bg-sky-50 h-14 px-8 rounded-xl font-bold shadow-xl hover:shadow-2xl hover:scale-110 transition-all duration-300" data-testid="contact-btn">
                Contact Us
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
              <Button className="bg-emerald-500 hover:bg-emerald-600 text-white h-14 px-8 rounded-xl font-bold shadow-xl hover:shadow-2xl hover:scale-110 transition-all duration-300" data-testid="whatsapp-btn">
                <MessageCircle className="w-5 h-5 mr-2" />
                WhatsApp Us
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
